import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useLoad } from "@/lib/api";
import { useMe } from "@/lib/me";
import { fmtDate } from "@/lib/format";
import { PageHead, Empty } from "@/components/Bits";

const BADGE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-slate-100 text-slate-700 border-slate-200",
  rejected: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const WORD = { pending: "Waiting for owner", approved: "Deleted", rejected: "Kept" };

export default function ApprovalsPage() {
  const api = useApi();
  const owner = useMe().role === "owner";
  const list = useLoad("/deletions");
  const [busy, setBusy] = useState(null);
  const decide = async (r, approve) => {
    if (approve && !window.confirm(`Delete ${r.label}? This can't be undone.`)) return;
    setBusy(r.id);
    try {
      await api("post", `/deletions/${r.id}/decide`, { approve });
      toast.success(approve ? "Deleted" : "Request rejected — nothing was deleted");
      window.dispatchEvent(new Event("deletions-changed"));
      list.reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  return (
    <div>
      <PageHead title="Approvals" sub={owner ? "Deletes asked for by managers and staff. Nothing is removed until you approve." : "Deletes you and the team have asked for. An owner approves each one."} />
      {list.data?.length === 0 && <Empty icon={ShieldCheck} title="No delete requests" text="Requests to delete documents or wholesalers appear here." testid="approvals-empty" />}
      <div className="space-y-3" data-testid="approvals-list">
        {(list.data || []).map((r) => (
          <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4 rise" data-testid={`approval-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="break-words font-semibold" data-testid={`approval-label-${r.id}`}>{r.label}</p>
                <p className="text-xs text-slate-500">Asked by {r.requested_by} · {fmtDate(r.requested_at)}</p>
              </div>
              <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${BADGE[r.status]}`} data-testid={`approval-status-${r.id}`}>{WORD[r.status]}</span>
            </div>
            {r.status !== "pending" && r.decided_by && (
              <div className={`mt-3 rounded-xl border px-3 py-2 ${r.status === "approved" ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid={`approval-stamp-${r.id}`}>
                <p className={`text-xs font-extrabold uppercase tracking-wider ${r.status === "approved" ? "text-emerald-700" : "text-rose-700"}`}>{r.status === "approved" ? "Approved & deleted" : "Rejected — kept"}</p>
                <p className="text-xs text-slate-600">by {r.decided_by}{r.decided_by_email && r.decided_by_email !== r.decided_by ? ` (${r.decided_by_email})` : ""} · {fmtDate(r.decided_at)}</p>
              </div>
            )}
            {r.reason && <p className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">"{r.reason}"</p>}
            {owner && r.status === "pending" && (
              <div className="mt-3 flex gap-2">
                <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={busy === r.id} onClick={() => decide(r, true)} data-testid={`approval-approve-${r.id}`}><Check className="mr-1 h-4 w-4" /> Approve delete</Button>
                <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => decide(r, false)} data-testid={`approval-reject-${r.id}`}><X className="mr-1 h-4 w-4" /> Keep it</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
